import _ from "lodash";
import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";

import { adminRequests } from "@/api/(requests)/admin";
import { isAuthorized } from "@/utils/authToken";
import { patientRequests } from "@/api/(requests)/patient";

import { calculateAge } from "../../../utils/momentUtils";
import { Card, InsuranceEligibilityRecord, Link } from "../../../component-library";
import { CompletedProfileInsurance } from "../../../component-library/MemberProfileInsurance";
import {
  formatInsuranceType,
  formatTimestamp,
  getFormattedStatus,
  getStatusClass,
  getStatusDefinition,
  showRawResponse,
} from "../../../component-library/InsuranceEligibilityRecord/utils";
import { formatMobileNumber } from "../../../utils/filters";
import {
  getInsuranceImagePresignedUrl,
  INSURANCE_IMAGE_FILE_PRIMARY_BACK_SUB_TYPE,
  INSURANCE_IMAGE_FILE_PRIMARY_FRONT_SUB_TYPE,
  PATIENT_INSURANCE_IMAGE_FILE_TYPE,
} from "../../../utils/fileUtil";
import ImageFullPreview from "../../Common/ImageFullPreview";

import "./styles.scss";

const InsuranceEligibilityRecordManagement = (props) => {
  const [eligibilityRecord, setEligibilityRecord] = useState({
    carrier: "",
    check_type: "",
    email: "",
    errors: [],
    id: "",
    insurance_details: {
      card_back: "",
      card_front: "",
      dob: "",
      first_name: "",
      group_id: "",
      last_name: "",
      member_id: "",
      policy_dob: "",
      policy_holder_name: "",
      policy_relationship: "",
      state: "",
    },
    insurance_type: "",
    mobile: "",
    number_of_attempts: 0,
    raw_response_exists: false,
    raw_response_key: "",
    registration_date: "",
    status: "",
    timestamp: "",
    username: "",
  });

  const [insuranceCardImgInfo, setInsuranceCardImgInfo] = useState({
    backContentType: "",
    backSrcUrl: "",
    frontContentType: "",
    frontSrcUrl: "",
  });

  const [imgFullPreview, setImgFullPreview] = useState({
    contentType: null,
    url: null,
  });

  async function getInsuranceEligibilityRecord() {
    const data = {
      id: props.match.params.id,
      query_type: "id",
    };
    const eligibilityResponse = await adminRequests.fetch_insurance_eligibility_records({ data });

    if (!eligibilityResponse) {
      return;
    }

    if (!eligibilityResponse.insurance_details) {
      eligibilityResponse["insurance_details"] = eligibilityRecord.insurance_details;
    }
    setEligibilityRecord(eligibilityResponse);

    return eligibilityResponse;
  }

  useEffect(() => {
    (async () => {
      const eligibilityResponse = await getInsuranceEligibilityRecord();
      const insuranceDetails = eligibilityResponse.insurance_details;

      if (!insuranceDetails) {
        return;
      }
      const newInsuranceCardImgInfo = _.cloneDeep(insuranceCardImgInfo);
      const frontImgKey = insuranceDetails.card_front;

      if (frontImgKey) {
        const frontImgResponse = await getInsuranceImagePresignedUrl(
          eligibilityResponse.username,
          PATIENT_INSURANCE_IMAGE_FILE_TYPE,
          INSURANCE_IMAGE_FILE_PRIMARY_FRONT_SUB_TYPE,
        );

        newInsuranceCardImgInfo["frontSrcUrl"] = frontImgResponse.message;
        newInsuranceCardImgInfo["frontSrcStatus"] = frontImgResponse.status;
        newInsuranceCardImgInfo["frontContentType"] = frontImgResponse.content_type;
      }

      const backImgKey = insuranceDetails.card_back;

      if (backImgKey) {
        const backImgResponse = await getInsuranceImagePresignedUrl(
          eligibilityResponse.username,
          PATIENT_INSURANCE_IMAGE_FILE_TYPE,
          INSURANCE_IMAGE_FILE_PRIMARY_BACK_SUB_TYPE,
        );

        newInsuranceCardImgInfo["backSrcUrl"] = backImgResponse.message;
        newInsuranceCardImgInfo["backSrcStatus"] = backImgResponse.status;
        newInsuranceCardImgInfo["backContentType"] = backImgResponse.content_type;
      }
      setInsuranceCardImgInfo(newInsuranceCardImgInfo);
    })();
  }, []);

  const runEligibilityCheck = async () => {
    const data = eligibilityRecord.insurance_details;

    data["check_type"] = "manual_admin";
    //todo change to v2
    await patientRequests.fetch_insurance_eligibility_v2({ data });
    await getInsuranceEligibilityRecord();
  };

  const formatFullName = (insuranceDetails) => {
    return `${insuranceDetails.first_name || ""} ${insuranceDetails.last_name || ""}`;
  };

  const formatDOB = (insuranceDetails) => {
    if (!insuranceDetails.dob) {
      return "";
    }

    return `${eligibilityRecord.insurance_details.dob} (${calculateAge(
      eligibilityRecord.insurance_details.dob,
    )})`;
  };

  const formatMobile = (eligibilityRecord) => {
    if (!eligibilityRecord.mobile) {
      return "";
    }

    return formatMobileNumber(eligibilityRecord.mobile);
  };

  const formatPatientInfo = (eligibilityRecord) => {
    if (!eligibilityRecord.email) {
      return;
    }

    const insuranceDetails = eligibilityRecord.insurance_details;

    if (!eligibilityRecord.mobile) {
      return `${formatDOB(insuranceDetails)} • ${eligibilityRecord.email}`;
    }

    return `${formatDOB(insuranceDetails)} • ${formatMobile(eligibilityRecord)} • ${
      eligibilityRecord.email
    }`;
  };

  const formatRegistrationDate = (registrationDate) => {
    if (!registrationDate) {
      return;
    }

    return `Registered on: ${formatTimestamp(eligibilityRecord.registration_date)}`;
  };

  return (
    <>
      <div className="container my-5">
        <div className="pb-3">
          <Card className="insurance-eligibility-patient-info">
            <div className="d-flex justify-content-between">
              <div className="fs-18 tw:font-bold">
                {formatFullName(eligibilityRecord.insurance_details)}
              </div>
              {eligibilityRecord.username && (
                <Link href={`/app/patient/${eligibilityRecord.username}/profile/v2`}>
                  View Profile
                </Link>
              )}
            </div>
            <div className="d-lg-none d-md-block d-sm-block">
              <div>{formatDOB(eligibilityRecord.insurance_details)}</div>
              <div>{formatMobile(eligibilityRecord)}</div>
              <div>{eligibilityRecord.email}</div>
            </div>
            <div className="d-lg-block d-md-none d-none">
              <span>{formatPatientInfo(eligibilityRecord)}</span>
            </div>
            <div>
              <span>{formatRegistrationDate(eligibilityRecord.registration_date)}</span>
            </div>
          </Card>
        </div>

        <div className="pb-3">
          {imgFullPreview.url && (
            <ImageFullPreview
              close={() =>
                setImgFullPreview({
                  contentType: null,
                  url: null,
                })
              }
              contentType={imgFullPreview.contentType}
              url={imgFullPreview.url}
            />
          )}

          <Card>
            <CompletedProfileInsurance
              groupId={eligibilityRecord.insurance_details.group_id}
              insuranceCardImgBackSrc={insuranceCardImgInfo.backSrcUrl}
              insuranceCardImgBackStatus={insuranceCardImgInfo.backSrcStatus}
              insuranceCardImgFrontSrc={insuranceCardImgInfo.frontSrcUrl}
              insuranceCardImgFrontStatus={insuranceCardImgInfo.frontSrcStatus}
              insuranceCompany={eligibilityRecord.carrier}
              isAso={eligibilityRecord.is_aso}
              isEditable={false}
              memberId={eligibilityRecord.insurance_details.member_id}
              onInsuranceCardImgBackClick={() =>
                setImgFullPreview({
                  contentType: insuranceCardImgInfo.backContentType,
                  url: insuranceCardImgInfo.backSrcUrl,
                })
              }
              onInsuranceCardImgFrontClick={() =>
                setImgFullPreview({
                  contentType: insuranceCardImgInfo.frontContentType,
                  url: insuranceCardImgInfo.frontSrcUrl,
                })
              }
              policyHolder={eligibilityRecord.insurance_details.policy_relationship}
              policyHolderDOB={eligibilityRecord.insurance_details.policy_dob}
              policyHolderName={eligibilityRecord.insurance_details.policy_holder_name}
              stateOfInsurancePlan={eligibilityRecord.insurance_details.state}
              title="Insurance Information"
            />
          </Card>
        </div>

        <InsuranceEligibilityRecord
          allowRetry={true}
          carrier={eligibilityRecord.carrier}
          className={getStatusClass(eligibilityRecord.status)}
          copay={eligibilityRecord.copay}
          downloadResponseButtonOnClick={() => showRawResponse(eligibilityRecord)}
          errors={eligibilityRecord.errors}
          insuranceType={formatInsuranceType(eligibilityRecord.insurance_type)}
          numberOfAttempts={eligibilityRecord.number_of_attempts}
          plan={eligibilityRecord.plan}
          rawResponseKey={eligibilityRecord.raw_response_key}
          retryButtonOnClick={runEligibilityCheck}
          showDownloadRawResponse={
            isAuthorized("admin:customer_success_executive") &&
            eligibilityRecord.raw_response_exists
          }
          status={getFormattedStatus(eligibilityRecord.status)}
          statusDefinition={getStatusDefinition(eligibilityRecord.status)}
          timestamp={formatTimestamp(eligibilityRecord.timestamp)}
          transactionId={eligibilityRecord.control_number}
        />
      </div>
    </>
  );
};

export default withRouter(InsuranceEligibilityRecordManagement);
